/**
 * Generic E-commerce Tracker
 * Used for platforms without specific implementation
 * Uses common patterns and heuristics to track e-commerce events
 */
import BaseEcommerceTracker from './base';

export default class GenericEcommerceTracker extends BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    super(b2mTracker, platformName || 'Generic');
    
    // Generic selectors that work across many platforms
    this.selectors = {
      // Product page indicators
      productDetail: [
        '.product-detail', '.product-page', '.pdp',
        '[data-product]', '[itemtype*="Product"]',
        '.product-single', '.product-info', '.urun-detay'
      ].join(', '),
      
      productName: [
        '.product-name', '.product-title', '.product__title',
        '[itemprop="name"]', 'h1.title', '.pdp-title',
        '.urun-adi', '[data-product-name]'
      ].join(', '),
      
      productPrice: [
        '.product-price', '.price', '.current-price',
        '[itemprop="price"]', '.sale-price', '.final-price',
        '.fiyat', '[data-price]', '.amount'
      ].join(', '),
      
      productImage: [
        '.product-image img', '.product-gallery img',
        '[itemprop="image"]', '.pdp-image img'
      ].join(', '),
      
      // Add to cart
      addToCartButton: [
        '.add-to-cart', '.btn-cart', '[data-action="add-to-cart"]',
        'button[name="add"], .sepete-ekle', '.add-to-bag',
        '[data-add-to-cart]', '.cart-button'
      ].join(', '),
      
      quantityInput: [
        'input[name="quantity"]', '.quantity input',
        '.qty', 'input.qty', '.adet'
      ].join(', '),
      
      // Cart
      cartPage: [
        '.cart-page', '.cart', '.shopping-cart',
        '.sepet', '[data-page="cart"]', '.basket'
      ].join(', '),
      
      cartItems: [
        '.cart-item', '.cart-product', '.line-item',
        '.sepet-urun', '.basket-item'
      ].join(', '),
      
      cartTotal: [
        '.cart-total', '.grand-total', '.order-total',
        '.toplam', '.subtotal'
      ].join(', '),
      
      cartItemRemove: [
        '.remove', '.delete', '.remove-item',
        '.sil', '[data-action="remove"]'
      ].join(', '),
      
      // Checkout
      checkoutPage: [
        '.checkout', '.checkout-page',
        '.odeme', '[data-page="checkout"]'
      ].join(', '),
      
      // Order confirmation
      orderConfirmation: [
        '.order-confirmation', '.thank-you', '.order-success',
        '.siparis-onay', '[data-page="order-success"]'
      ].join(', '),
      
      orderId: [
        '.order-id', '.order-number', '.confirmation-number',
        '.siparis-no'
      ].join(', '),
      
      // Category/List pages
      productList: [
        '.product-list', '.products', '.product-grid',
        '.category-products', '.urun-listesi'
      ].join(', '),
      
      productCard: [
        '.product-card', '.product-item', '.product',
        '.urun-kart', '.grid-item'
      ].join(', '),
      
      // Search
      searchInput: [
        'input[name="q"]', 'input[name="search"]',
        '.search-input', '[type="search"]', '.arama'
      ].join(', '),
      
      // Wishlist
      wishlistButton: [
        '.add-to-wishlist', '.wishlist-btn', '.favorite',
        '.favorilere-ekle', '[data-wishlist]'
      ].join(', '),
      
      // Coupon
      couponInput: [
        'input[name="coupon"]', '.coupon-input',
        '#coupon', '.kupon'
      ].join(', ')
    };
    
    // Generic URL patterns
    this.urlPatterns = {
      product: ['/product', '/urun', '/item', '/p/', '/-p-', '/detail'],
      category: ['/category', '/kategori', '/collection', '/c/', '/-c-', '/shop'],
      cart: ['/cart', '/sepet', '/basket', '/bag'],
      checkout: ['/checkout', '/odeme', '/payment'],
      orderConfirmation: ['/order-confirmation', '/thank-you', '/success', '/siparis-onay'],
      search: ['/search', '/arama', '?q=', '?search=']
    };
  }

  /**
   * Initialize generic tracking
   */
  init() {
    super.init();
    
    // Setup generic event tracking
    this.setupGenericAddToCart();
    this.setupGenericCartActions();
    this.setupGenericSearch();
    this.setupGenericWishlist();
    
    console.log(`[B2M ${this.platformName}] ✅ Generic e-commerce tracking initialized`);
  }

  /**
   * Get page type using multiple detection methods
   */
  getPageType(path) {
    const url = path.toLowerCase();
    
    // Check URL patterns
    for (const [type, patterns] of Object.entries(this.urlPatterns)) {
      for (const pattern of patterns) {
        if (url.includes(pattern)) {
          return type;
        }
      }
    }
    
    // Check for page indicators in DOM
    if (document.querySelector(this.selectors.productDetail)) return 'product';
    if (document.querySelector(this.selectors.cartPage)) return 'cart';
    if (document.querySelector(this.selectors.checkoutPage)) return 'checkout';
    if (document.querySelector(this.selectors.orderConfirmation)) return 'order-confirmation';
    if (document.querySelector(this.selectors.productList)) return 'category';
    
    return 'other';
  }

  /**
   * Auto-track product page
   */
  autoTrackProductPage() {
    this.waitForElement(this.selectors.productDetail, (container) => {
      const product = this.extractGenericProduct(container || document);
      
      if (product.item_id || product.item_name) {
        this.trackViewItem([product], {
          page_type: 'product_detail',
          source: 'generic_extraction'
        });
      }
    });
  }

  /**
   * Auto-track category page
   */
  autoTrackCategoryPage() {
    this.waitForElement(this.selectors.productList, (container) => {
      const items = this.extractGenericProductList(container);
      const categoryName = this.extractGenericCategoryName();
      
      if (items.length > 0) {
        this.trackViewItemList(items, categoryName, {
          page_type: 'category',
          source: 'generic_extraction'
        });
      }
      
      this.setupProductCardClicks(container);
    });
  }

  /**
   * Auto-track cart page
   */
  autoTrackCartPage() {
    this.waitForElement(this.selectors.cartPage, (container) => {
      const items = this.extractGenericCartItems(container);
      const total = this.extractGenericTotal(container, this.selectors.cartTotal);
      
      if (items.length > 0) {
        this.trackViewCart(items, total, 'TRY', {
          page_type: 'cart',
          source: 'generic_extraction'
        });
      }
    });
  }

  /**
   * Auto-track checkout page
   */
  autoTrackCheckoutPage() {
    this.waitForElement(this.selectors.checkoutPage, (container) => {
      const items = this.extractGenericCartItems(container);
      const total = this.extractGenericTotal(container, this.selectors.cartTotal);
      
      this.trackBeginCheckout(items, total, 'TRY', {
        page_type: 'checkout',
        source: 'generic_extraction'
      });
    });
  }

  /**
   * Auto-track order confirmation
   */
  autoTrackOrderConfirmation() {
    this.waitForElement(this.selectors.orderConfirmation, (container) => {
      const orderData = this.extractGenericOrderData(container);
      
      if (orderData.transaction_id || orderData.value) {
        this.trackPurchase(orderData, {
          page_type: 'order_confirmation',
          source: 'generic_extraction'
        });
      }
    });
  }

  /**
   * Auto-track search page
   */
  autoTrackSearchPage() {
    const searchTerm = this.getSearchTermFromURL();
    
    if (searchTerm) {
      this.trackSearch(searchTerm, {
        source: 'url_param'
      });
      
      this.waitForElement(this.selectors.productList, (container) => {
        const items = this.extractGenericProductList(container);
        
        if (items.length > 0) {
          this.trackViewItemList(items, `Search: ${searchTerm}`, {
            search_term: searchTerm,
            results_count: items.length
          });
        }
      });
    }
  }

  // ============================================
  // Generic Event Tracking Setup
  // ============================================

  /**
   * Setup generic add to cart tracking
   */
  setupGenericAddToCart() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.addToCartButton);
      if (!button) return;
      
      // Find product container
      const productContainer = button.closest(this.selectors.productDetail) ||
                               button.closest(this.selectors.productCard) ||
                               document;
      
      const product = this.extractGenericProduct(productContainer);
      const quantity = this.getQuantityValue(productContainer);
      
      if (product.item_id || product.item_name) {
        product.quantity = quantity;
        this.trackAddToCart([product], product.price * quantity, 'TRY', {
          source: 'button_click'
        });
      }
    });

    // Listen for form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.querySelector(this.selectors.addToCartButton)) return;
      
      const product = this.extractGenericProduct(form);
      const quantity = this.getQuantityValue(form);
      
      if (product.item_id || product.item_name) {
        product.quantity = quantity;
        this.trackAddToCart([product], product.price * quantity, 'TRY', {
          source: 'form_submit'
        });
      }
    });
  }

  /**
   * Setup generic cart actions
   */
  setupGenericCartActions() {
    // Remove from cart
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.cartItemRemove);
      if (!button) return;
      
      const cartItem = button.closest(this.selectors.cartItems);
      if (cartItem) {
        const product = this.extractGenericProduct(cartItem);
        this.trackRemoveFromCart([product], {
          source: 'remove_click'
        });
      }
    });

    // Quantity change
    document.addEventListener('change', (e) => {
      const input = e.target.closest(this.selectors.quantityInput);
      if (!input) return;
      
      const cartItem = input.closest(this.selectors.cartItems);
      if (cartItem) {
        const product = this.extractGenericProduct(cartItem);
        product.quantity = parseInt(input.value) || 1;
        
        this.b2mTracker.track('cart_quantity_change', {
          ecommerce_platform: this.platformName,
          ...product
        });
      }
    });
  }

  /**
   * Setup generic search tracking
   */
  setupGenericSearch() {
    const searchInput = document.querySelector(this.selectors.searchInput);
    if (!searchInput) return;
    
    const searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        const searchTerm = searchInput.value.trim();
        if (searchTerm) {
          this.trackSearch(searchTerm, {
            source: 'form_submit'
          });
        }
      });
    }
  }

  /**
   * Setup generic wishlist tracking
   */
  setupGenericWishlist() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.wishlistButton);
      if (!button) return;
      
      const productContainer = button.closest(this.selectors.productDetail) ||
                               button.closest(this.selectors.productCard) ||
                               document;
      
      const product = this.extractGenericProduct(productContainer);
      
      if (product.item_id || product.item_name) {
        this.trackAddToWishlist([product], {
          source: 'button_click'
        });
      }
    });
  }

  // ============================================
  // Generic Data Extraction Methods
  // ============================================

  /**
   * Extract product using multiple strategies
   */
  extractGenericProduct(container) {
    const product = {
      item_id: null,
      item_name: null,
      price: 0,
      item_brand: null,
      item_category: null,
      currency: 'TRY'
    };
    
    // Strategy 1: Data attributes
    product.item_id = this.findDataAttribute(container, [
      'product-id', 'productId', 'product_id', 'id', 'sku',
      'urun-id', 'urunId', 'item-id'
    ]);
    
    product.item_name = this.findDataAttribute(container, [
      'product-name', 'productName', 'product_name', 'name', 'title',
      'urun-adi', 'urunAdi', 'item-name'
    ]);
    
    // Strategy 2: Schema.org markup
    const schemaProduct = container.querySelector('[itemtype*="schema.org/Product"]') || container;
    const schemaName = schemaProduct.querySelector('[itemprop="name"]');
    const schemaPrice = schemaProduct.querySelector('[itemprop="price"]');
    const schemaBrand = schemaProduct.querySelector('[itemprop="brand"]');
    const schemaSku = schemaProduct.querySelector('[itemprop="sku"]');
    
    product.item_name = product.item_name || schemaName?.textContent?.trim();
    product.item_id = product.item_id || schemaSku?.textContent?.trim();
    product.item_brand = schemaBrand?.textContent?.trim();
    
    if (schemaPrice) {
      product.price = parseFloat(schemaPrice.content || schemaPrice.textContent) || 0;
    }
    
    // Strategy 3: Common CSS selectors
    if (!product.item_name) {
      const nameEl = container.querySelector(this.selectors.productName);
      product.item_name = nameEl?.textContent?.trim();
    }
    
    if (!product.price) {
      const priceEl = container.querySelector(this.selectors.productPrice);
      product.price = this.parsePrice(priceEl?.textContent);
    }
    
    // Strategy 4: Try window globals
    if (window.productData) {
      product.item_id = product.item_id || window.productData.id;
      product.item_name = product.item_name || window.productData.name;
      product.price = product.price || window.productData.price;
      product.item_brand = product.item_brand || window.productData.brand;
    }
    
    // Strategy 5: JSON-LD
    if (!product.item_name) {
      const jsonLd = this.extractJsonLd();
      if (jsonLd) {
        product.item_id = product.item_id || jsonLd.sku || jsonLd.productID;
        product.item_name = product.item_name || jsonLd.name;
        product.price = product.price || parseFloat(jsonLd.offers?.price) || 0;
        product.item_brand = product.item_brand || jsonLd.brand?.name;
      }
    }
    
    // Strategy 6: Extract from URL for product ID
    if (!product.item_id) {
      const urlMatch = window.location.pathname.match(/[-_](\d+)(?:\/|$|\?)/);
      if (urlMatch) {
        product.item_id = urlMatch[1];
      }
    }
    
    // Get category from breadcrumb
    product.item_category = this.extractGenericCategoryName();
    
    return product;
  }

  /**
   * Extract product list
   */
  extractGenericProductList(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    const items = [];
    
    productCards.forEach((card, index) => {
      const product = this.extractGenericProduct(card);
      product.index = index;
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract cart items
   */
  extractGenericCartItems(container) {
    const cartItems = container.querySelectorAll(this.selectors.cartItems);
    const items = [];
    
    cartItems.forEach((item, index) => {
      const product = this.extractGenericProduct(item);
      
      // Get quantity
      const quantityEl = item.querySelector(this.selectors.quantityInput);
      product.quantity = parseInt(quantityEl?.value) || 1;
      product.index = index;
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract total from container
   */
  extractGenericTotal(container, selector) {
    const totalEl = container.querySelector(selector);
    if (totalEl) {
      return this.parsePrice(totalEl.textContent);
    }
    return 0;
  }

  /**
   * Extract order data
   */
  extractGenericOrderData(container) {
    const orderData = {
      transaction_id: null,
      value: 0,
      currency: 'TRY',
      items: []
    };
    
    // Get order ID
    const orderIdEl = container.querySelector(this.selectors.orderId);
    if (orderIdEl) {
      orderData.transaction_id = orderIdEl.textContent.trim().replace(/[^\d]/g, '');
    }
    
    // Try data attribute
    if (!orderData.transaction_id) {
      orderData.transaction_id = this.findDataAttribute(container, [
        'order-id', 'orderId', 'order_id', 'transaction-id'
      ]);
    }
    
    // Get total
    const totalEl = container.querySelector('.order-total, .grand-total, .toplam');
    if (totalEl) {
      orderData.value = this.parsePrice(totalEl.textContent);
    }
    
    // Get items if available
    orderData.items = this.extractGenericCartItems(container);
    
    // Try window globals
    if (window.orderData) {
      orderData.transaction_id = orderData.transaction_id || window.orderData.id;
      orderData.value = orderData.value || window.orderData.total;
    }
    
    return orderData;
  }

  /**
   * Extract category name from breadcrumb or page
   */
  extractGenericCategoryName() {
    // Try breadcrumb
    const breadcrumb = document.querySelector('.breadcrumb, .breadcrumbs, [class*="breadcrumb"]');
    if (breadcrumb) {
      const items = breadcrumb.querySelectorAll('a, span');
      const categories = Array.from(items)
        .map(item => item.textContent.trim())
        .filter(text => text && !['home', 'ana sayfa', 'anasayfa', '>'].includes(text.toLowerCase()));
      
      if (categories.length > 0) {
        return categories.join(' > ');
      }
    }
    
    // Try page title
    const pageTitle = document.querySelector('h1.category-title, h1.page-title, .archive-title');
    if (pageTitle) {
      return pageTitle.textContent.trim();
    }
    
    return null;
  }

  /**
   * Get search term from URL
   */
  getSearchTermFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || params.get('search') || params.get('s') || 
           params.get('arama') || params.get('kelime');
  }

  /**
   * Get quantity value
   */
  getQuantityValue(container) {
    const quantityEl = container.querySelector(this.selectors.quantityInput);
    return parseInt(quantityEl?.value) || 1;
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Find data attribute with multiple possible names
   */
  findDataAttribute(element, possibleNames) {
    // Check element's own data attributes
    for (const name of possibleNames) {
      const camelCase = name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      if (element.dataset?.[camelCase]) {
        return element.dataset[camelCase];
      }
    }
    
    // Check child elements
    for (const name of possibleNames) {
      const el = element.querySelector(`[data-${name}]`);
      if (el) {
        const camelCase = name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        return el.dataset[camelCase];
      }
    }
    
    return null;
  }

  /**
   * Extract JSON-LD product data
   */
  extractJsonLd() {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        
        // Handle array format
        const items = Array.isArray(data) ? data : [data];
        
        for (const item of items) {
          if (item['@type'] === 'Product') {
            return item;
          }
          // Check @graph format
          if (item['@graph']) {
            const product = item['@graph'].find(i => i['@type'] === 'Product');
            if (product) return product;
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return null;
  }

  /**
   * Setup product card clicks
   */
  setupProductCardClicks(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    
    productCards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Don't track if clicking on action buttons
        if (e.target.closest(this.selectors.addToCartButton) ||
            e.target.closest(this.selectors.wishlistButton)) {
          return;
        }
        
        const product = this.extractGenericProduct(card);
        product.index = index;
        
        if (product.item_id || product.item_name) {
          this.trackSelectItem([product], {
            source: 'product_card_click'
          });
        }
      });
    });
  }

  /**
   * Wait for element with timeout
   */
  waitForElement(selector, callback, timeout = 5000) {
    // Handle comma-separated selectors
    const selectors = selector.split(', ');
    
    const findElement = () => {
      for (const sel of selectors) {
        const el = document.querySelector(sel.trim());
        if (el) return el;
      }
      return null;
    };
    
    const element = findElement();
    
    if (element) {
      callback(element);
      return;
    }
    
    const observer = new MutationObserver((mutations, obs) => {
      const el = findElement();
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    setTimeout(() => observer.disconnect(), timeout);
  }
}
